import React, {
  RefObject, useEffect, useRef, useState,
} from 'react';
import YouTube from 'react-youtube';
import { Text } from '@components';
import styles from './styles.module.scss';

type SectionKeyProps = {
  refSection: RefObject<HTMLDivElement>;
};

const videos = [
  'wswOQAkLn1s',
  'zDR6UE7_96Q',

];

const SectionInstruction = ({ refSection }: SectionKeyProps) => {
  const section = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isMobile = window.innerWidth < 1440;

  const playerRefs = useRef<any[]>([]);

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (track) {
      const child = track.children[index] as HTMLElement;
      if (child) {
        const { offsetLeft } = child;
        const center = track.clientWidth / 2 - child.clientWidth / 2;
        track.scrollTo({ left: offsetLeft - center, behavior: 'smooth' });
      }
    }
  };

  const handlePrev = () => {
    if (playingIndex !== null) {
      playerRefs.current[playingIndex]?.stopVideo();
      setPlayingIndex(null);
    }
    const newIndex = Math.max(currentIndex - 1, 0);
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    if (playingIndex !== null) {
      playerRefs.current[playingIndex]?.stopVideo();
      setPlayingIndex(null);
    }
    const newIndex = Math.min(currentIndex + 1, videos.length - 1);
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const handleStateChange = (event: any, index: number) => {
    const state = event.data;

    if (state === 1) {
      playerRefs.current.forEach((player, i) => {
        if (i !== index) {
          player?.pauseVideo();
        }
      });

      setCurrentIndex(index);

      if (!isMobile) {
        scrollToIndex(index);
      }

      setPlayingIndex(index);
    }

    if (state === 2 || state === 0) {
      if (playingIndex === index) {
        setPlayingIndex(null);
      }
    }
  };

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      if (playingIndex === null) return;

      const activeCard = trackRef.current?.children[playingIndex] as HTMLElement;
      if (!activeCard) return;

      const iframe = activeCard.querySelector('iframe');
      const clickedElement = event.target as HTMLElement;

      const isClickInsideCard = activeCard.contains(clickedElement);
      const isClickOnIframe = clickedElement === iframe;

      if (!isClickInsideCard || !isClickOnIframe) {
        playerRefs.current[playingIndex]?.pauseVideo();
        setPlayingIndex(null);
      }
    };

    document.addEventListener('click', handleGlobalClick);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [playingIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);

      // Подождём завершения скролла (debounce)
      scrollTimeout = setTimeout(() => {
        const children = Array.from(track.children) as HTMLElement[];
        const trackCenter = track.scrollLeft + track.clientWidth / 2;

        // Найдём ближайший элемент к центру
        const closestIndex = children.reduce((closestIdx, child, idx) => {
          const childCenter = child.offsetLeft + child.clientWidth / 2;
          const distance = Math.abs(trackCenter - childCenter);
          const prevDistance = Math.abs(
            trackCenter - (children[closestIdx].offsetLeft + children[closestIdx].clientWidth / 2),
          );
          return distance < prevDistance ? idx : closestIdx;
        }, 0);

        // Обновим currentIndex
        setCurrentIndex(closestIndex);

        // Можно подровнять до идеально отцентрированного:
        scrollToIndex(closestIndex);
      }, 100); // подстрой под скорость скролла
    };

    track.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      track.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return (
    <section className={styles.containerKey} ref={section}>
      <div ref={refSection} className={styles.keyAnchor} />
      <div className={styles.content}>
        <Text type="h2" className={styles.title}>
          <span>Getting</span>
          <br />
          <span>Started</span>
        </Text>

        <div className={styles.videosSlider}>
          <div className={styles.videoTrack} ref={trackRef}>
            {videos.map((videoId, index) => (
              <div className={styles.videoCardWrapper} key={videoId}>
                <div
                  className={`${styles.videoCard} ${index === playingIndex ? styles.active : ''}`}
                >
                  <YouTube
                    videoId={videoId}
                    opts={{
                      width: '100%',
                      height: '100%',
                      playerVars: {
                        autoplay: 0,
                        enablejsapi: 1,
                        origin: window.location.origin,
                      },
                    }}
                    onStateChange={(event: any) => handleStateChange(event, index)}
                    onReady={(event: any) => {
                      playerRefs.current[index] = event.target;
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${styles.sliderControls} ${playingIndex !== null ? styles.controlsDown : ''}`}>
          <button className={currentIndex === 0 ? styles.disabled : ''} type="button" onClick={handlePrev} disabled={currentIndex === 0}>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
              <path d="M1 1L9 8L1 15" stroke="#DCDCDC" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" className={currentIndex === videos.length - 1 ? styles.disabled : ''} onClick={handleNext} disabled={currentIndex === videos.length - 1}>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
              <path d="M1 1L9 8L1 15" stroke="#DCDCDC" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export { SectionInstruction };
